// ConfigForm.tsx
import * as React from 'react'
import { TextField, MenuItem, Autocomplete, Chip, Grid, Box, Typography } from '@mui/material'
import { FieldConfig, FieldType } from 'components/Forms/types'

type Props = {
  config: Record<string, FieldConfig> // merged top + extra
  values: Record<string, any>
  onChange: (key: string, val: any) => void
  disabled?: boolean
  columns?: 2 | 1
}

const ConfigForm: React.FC<Props> = ({ config, values, onChange, disabled, columns = 2 }) => {
  const keys = Object.keys(config)
  if (keys.length === 0) return null

  return (
    <Grid container spacing={2} sx={{ px: 1, py: 1 }}>
      {keys.map(key => {
        const cfg = config[key]
        const half = columns === 2 ? 6 : 12
        return (
          <Grid key={key} item xs={12} md={half}>
            <Field keyName={key} cfg={cfg} value={values[key]} onChange={onChange} disabled={disabled} />
          </Grid>
        )
      })}
    </Grid>
  )
}

export default ConfigForm

function Field({
  keyName,
  cfg,
  value,
  onChange,
  disabled,
}: {
  keyName: string
  cfg: FieldConfig
  value: any
  onChange: (k: string, v: any) => void
  disabled?: boolean
}) {
  const common = {
    label: cfg.label,
    fullWidth: true,
    disabled,
    helperText: cfg.placeholder ?? '',
  }

  switch (cfg.type) {
    case FieldType.TEXT:
      return <TextField {...common} value={value ?? ''} onChange={e => onChange(keyName, e.target.value)} />
    case FieldType.TEXTAREA:
      return (
        <TextField
          {...common}
          value={value ?? ''}
          onChange={e => onChange(keyName, e.target.value)}
          multiline
          minRows={3}
        />
      )
    case FieldType.NUMBER:
      return (
        <TextField
          {...common}
          value={value ?? ''}
          type="number"
          onChange={e => onChange(keyName, e.target.value === '' ? '' : Number(e.target.value))}
        />
      )
    case FieldType.SELECT:
      return (
        <TextField {...common} select value={value ?? ''} onChange={e => onChange(keyName, e.target.value)}>
          {(cfg.options ?? []).map(opt => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </TextField>
      )
    case FieldType.MULTISELECT:
      return (
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={(cfg.options ?? []) as string[]}
          value={Array.isArray(value) ? value : []}
          onChange={(_, newVal) => onChange(keyName, newVal)}
          renderTags={(val, getTagProps) =>
            val.map((opt, i) => <Chip {...getTagProps({ index: i })} key={opt} label={opt} />)
          }
          renderInput={params => <TextField {...params} {...common} />}
        />
      )
    case FieldType.COLOR_PICKER:
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: 56 }}>
          <input
            type="color"
            value={value || '#000000'}
            onChange={e => onChange(keyName, e.target.value)}
            disabled={disabled}
            style={{ width: 44, height: 36, border: 'none', background: 'transparent', padding: 0 }}
          />
          <Typography variant="body2">{cfg.label}</Typography>
        </Box>
      )
    default:
      return <TextField {...common} value={value ?? ''} onChange={e => onChange(keyName, e.target.value)} />
  }
}
